/**
 * A React component that displays a summary of shipment statistics for a given week.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.dateStart] - The start date of the week to query for, formatted as 'YYYY-MM-DD'. Defaults to the start of the current week (weeks start on Mondays).
 * @param {string} [props.dateEnd] - The end date of the week to query for, formatted as 'YYYY-MM-DD'. Defaults to the end of the current week.
 *
 * @returns {JSX.Element} - A React component that contains:
 *  1. A data grid with summary stats of shipments occurring by week, organized by customer.
 *  2. A card displaying the number of customers.
 *  3. A card displaying the number of deliveries.
 */

import React, { useState } from "react";
import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";

import { Box, Button } from "@mui/material";
import { endOfWeek, startOfWeek } from "date-fns";
import { Link, useSearchParams } from "react-router-dom";

import { useError } from "../../contexts/ErrorContext";
import { formatDateYYYYMMDD } from "utils/formatting";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";
import { renderNumberCell, renderPercentCell } from "utils/dataGrid";
import { useShippingDataCoordinator } from "../../hooks/useShippingDataCoordinator";
import ShippingStatusSummary from "./ShippingStatusSummary";
import ShippingSummaryStatusButton from "./ShippingSummaryStatusButton";
import { RiExternalLinkLine, RiSendPlaneLine } from "@remixicon/react";
import ShippingManager from "./ShippingManager";

interface ShippingSummaryByWeekProps {
  dateStart?: string;
  dateEnd?: string;
  overviewData?: any[];
  isLoading?: boolean;
}

// Helper function to calculate percentage progress
const calculateProgress = (completed: number, total: number): number | null => {
  return total > 0 ? (completed / total) * 100 : null;
};

const ShippingSummaryByWeek: React.FC<ShippingSummaryByWeekProps> = (props) => {
  const { showError } = useError();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerIdFromURL = searchParams.get("customerId");
  const [showCustomerId, setShowCustomerId] = useState<number | null>(
    customerIdFromURL ? Number(customerIdFromURL) : null
  );

  const [showExportManager, setShowExportManager] = useState<boolean>(false);
  const [customerName, setCustomerName] = useState<string | null>(null);

  const today = new Date();
  const {
    dateStart = formatDateYYYYMMDD(startOfWeek(today, { weekStartsOn: 1 })),
    dateEnd = formatDateYYYYMMDD(endOfWeek(today, { weekStartsOn: 0 })),
    overviewData: propsOverviewData,
    isLoading: propsIsLoading,
  } = props;

  const {
    overviewData: localOverviewData,
    isLoading: localIsLoading,
    overviewError: error,
  } = useShippingDataCoordinator(dateStart, dateEnd);

  // Use props if available, otherwise fall back to hook's data
  const data = propsOverviewData ?? localOverviewData;
  const isLoading = propsIsLoading ?? localIsLoading;

  if (error) {
    showError(`Error fetching shipping summary for ${dateStart} - ${dateEnd}.`);
  }
  const defaultGridState: GridInitialState = {
    filter: {
      filterModel: {
        items: [],
      },
    },
    sorting: { sortModel: [{ field: "status_sort_order", sort: "asc" }] },
    pagination: { paginationModel: { pageSize: 25, page: 0 } },
    pinnedColumns: { left: [] },
    columns: { columnVisibilityModel: {} },
    density: "compact",
    aggregation: {
      model: {
        customer_name: "size",
        count_delivery_fulfillments: "sum",
        count_volume_upgrades: "sum",
        exports_progress: "avg",
        fulfillments_progress: "avg",
        vu_created_progress: "avg",
        vu_merged_progress: "avg",
        summary_status: "hideAggregation",
      },
    },
  };

  const columns_def: GridColDef[] = [
    {
      field: "customer_name",
      headerName: "Customer",
      width: 200,
      type: "string",
      renderCell: (params: GridRenderCellParams) => {
        if (params.aggregation) return params.formattedValue;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Button
              size="small"
              disabled={isLoading}
              disableRipple
              onClick={() =>
                handleShowCustomerId(
                  params.row.customer_id,
                  params.row.customer_name
                )
              }
            >
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                style={{ textTransform: "capitalize", fontSize: ".9rem" }}
              >
                <RiSendPlaneLine size={14} />{" "}
                <span style={{ textDecoration: "underline" }}>
                  {params.row.customer_name}
                </span>
              </Box>
            </Button>
            <Link to={`/customer/${params.row.customer_id}/deliveries`}>
              <RiExternalLinkLine size={16} />
            </Link>
          </Box>
        );
      },
    },
    { field: "fulfillment_date", headerName: "Fulfillment Date", width: 150 },
    { field: "fulfillment_date_dy", headerName: "Fulfillment Day", width: 150 },
    { field: "fulfillment_type", headerName: "Fulfillment Type", width: 150 },
    {
      field: "summary_status",
      headerName: "Status Summary",
      width: 250,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? (
          <ShippingStatusSummary status={params.value} />
        ) : null;
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      sortable: false,
      valueGetter: (value, row) => {
        // TODO: move this logic to the backend
        const hasError =
          row.count_exports_failed > 0 ||
          row.count_fulfillments_failed > 0 ||
          row.count_checksum_failed > 0;
        const hasWarning = row.count_exports_canceled > 0;

        return {
          statusType: hasError ? "error" : hasWarning ? "warning" : "success",
          customerId: row.customer_id,
          dateStart,
          dateEnd,
        };
      },
      renderCell: (params: GridRenderCellParams) => {
        return params.value.customerId ? (
          <ShippingSummaryStatusButton {...params.value} />
        ) : null;
      },
    },
    {
      field: "count_delivery_fulfillments",
      headerName: "Fulfillments Scheduled",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "count_volume_upgrades",
      headerName: "VUs Scheduled",
      description: "# of deliveries scheduled for volume upgrade",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "vu_started_progress",
      headerName: "VUs Started",
      description: "# deliveries submitted to VU pipeline / # VUs scheduled",
      width: 150,
      align: "right",
      headerAlign: "right",
      type: "number",
      valueGetter: (value, row) =>
        calculateProgress(row.count_vu_started, row.count_volume_upgrades),
      renderCell: renderPercentCell,
    },
    {
      field: "vu_created_progress",
      headerName: "VUs Created",
      description: "# VUs successfully created + approved / # VUs scheduled",
      width: 150,
      align: "right",
      headerAlign: "right",
      type: "number",
      valueGetter: (value, row) =>
        calculateProgress(row.count_vu_created, row.count_volume_upgrades),
      renderCell: renderPercentCell,
    },
    {
      field: "vu_merged_progress",
      headerName: "VUs Merged",
      description: "# VUs merged / # VUs Created",
      width: 150,
      align: "right",
      headerAlign: "right",
      type: "number",
      valueGetter: (value, row) => {
        const { count_vu_created, count_vu_merged, count_volume_upgrades } =
          row;
        if (count_vu_created > 0) {
          return calculateProgress(count_vu_merged, count_vu_created);
        } else if (count_volume_upgrades > 0) {
          return 0;
        }
        return null;
      },
      renderCell: renderPercentCell,
    },
    {
      field: "exports_progress",
      headerName: "Exports Progress",
      width: 150,
      align: "right",
      headerAlign: "right",
      type: "number",
      valueGetter: (value, row) =>
        calculateProgress(
          row.count_exports_completed,
          row.count_delivery_fulfillments
        ),
      renderCell: renderPercentCell,
    },
    {
      field: "fulfillments_progress",
      headerName: "Fulfillments Progress",
      width: 175,
      align: "right",
      headerAlign: "right",
      type: "number",
      valueGetter: (value, row) =>
        calculateProgress(
          row.count_fulfillments_completed,
          row.count_delivery_fulfillments
        ),
      renderCell: renderPercentCell,
    },
    {
      field: "count_exports_failed",
      headerName: "Total Exports Failed",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "count_exports_canceled",
      headerName: "Total Exports Canceled",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "count_fulfillments_failed",
      headerName: "Total Fulfillments Failed",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
  ];

  const handleShowCustomerId = (
    customerId: number,
    customerName: string
  ): void => {
    setShowCustomerId(customerId);
    setCustomerName(customerName);
    setShowExportManager(true);
  };

  const handleCloseShippingManagerDialog = (): void => {
    setShowExportManager(false);
    setShowCustomerId(null);
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete("customerId");
      return newParams;
    });
  };

  return (
    <>
      <Box>
        <GaneshDataGrid
          loading={isLoading}
          data={data || []}
          columns={columns_def}
          gridId="ShippingSummaryByWeekGrid"
          defaultGridState={defaultGridState}
          customHeight="550px"
          showGridSaver={false}
          sx={{
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "transparent !important",
            },
          }}
        />
      </Box>
      {showExportManager && showCustomerId && (
        <ShippingManager
          customerId={showCustomerId}
          customerName={customerName || ""}
          dateStart={dateStart}
          dateEnd={dateEnd}
          onClose={handleCloseShippingManagerDialog}
        />
      )}
    </>
  );
};

export default ShippingSummaryByWeek;
